import { Box, Grid, IconButton, Tooltip, Typography } from '@mui/material'

import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import { useAuthContext } from 'contexts/auth.context'
import { useNavigate } from 'react-router-dom'
import { Funcionalidades } from 'utils/helpers/funcionalidades'

import creasImg from 'assets/creas.png'
import { StyledImg } from './styles'
import { CreasResponse } from 'types/creas.type'
import DialogAlert from 'components/Dialog/Alert'
import React, { useState } from 'react'

type CreasItemProps = {
  creas: CreasResponse
  handleDelete: (id: number) => Promise<void>
}
export const CreasItem: React.FC<CreasItemProps> = ({ creas, handleDelete }) => {
  const [openExcluded, setOpenExcluded] = useState<boolean>(false)
  const { handleHasPermission } = useAuthContext()
  const navigate = useNavigate()

  const handleEdit = (): void => navigate(`/novo-creas/${creas.id}`)

  const handleDeleteDialog = async () => {
    await handleDelete(creas.id).finally(() => setOpenExcluded(false))
  }

  const capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  }

  return (
    <Box position={'relative'} height={'22.5rem'}>
      <Box
        display={'flex'}
        flexDirection={'column'}
        sx={{
          backgroundColor: 'white',
          border: '1px solid #EAECEE',
          borderRadius: '0.75rem',
          padding: '1rem',
          height: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          ':hover': {
            position: 'absolute',
            height: 'auto',
            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            zIndex: '9',
          },
        }}
      >
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            flexDirection={'row'}
            justifyContent={'space-between'}
            justifyItems={'flex-end'}
            display={'flex'}
          >
            <StyledImg src={creasImg} />
            <Box>
              {handleHasPermission(Funcionalidades.creas.editar) && (
                <Tooltip title='Editar'>
                  <IconButton aria-label='edit' size='medium' onClick={handleEdit}>
                    <EditRoundedIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}
              {handleHasPermission(Funcionalidades.creas.excluir) && (
                <Tooltip title='Excluir'>
                  <IconButton
                    aria-label='delete'
                    size='medium'
                    onClick={() => setOpenExcluded(true)}
                  >
                    <DeleteOutlinedIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant={'subtitle1'} fontWeight={800} color={'primary.main'}>
              {creas.nome}
            </Typography>
          </Grid>
          <Grid item xs={12} display={'flex'} alignItems={'center'}>
            <AccountBoxOutlinedIcon sx={{ marginRight: 1 }} />
            <Typography variant={'subtitle2'} fontWeight={600}>
              Diretor(a): {creas.diretor}
            </Typography>
          </Grid>
          <Grid item xs={12} display={'flex'} alignItems={'start'}>
            <LocationOnOutlinedIcon sx={{ marginRight: 1 }} />
            <Typography variant={'body2'}>
              {creas.endereco.logradouro}, {creas.endereco.numero}, {creas.endereco.bairro} -{' '}
              {creas.endereco.uf} - CEP: {creas.endereco.cep}
              {creas.endereco.pontoReferencia && ` - ${creas.endereco.pontoReferencia}`}
            </Typography>
          </Grid>
          <Grid item xs={12} display={'flex'} alignItems={'center'}>
            <LocalPhoneOutlinedIcon sx={{ marginRight: 1 }} />
            <Typography variant={'body2'}>
              {creas.telefones?.map((tel) => tel.telefone).join(', ')}
            </Typography>
          </Grid>
          <Grid item xs={12} display={'flex'} alignItems={'center'}>
            <EmailOutlinedIcon sx={{ marginRight: 1 }} />
            <Typography variant={'body2'}>{creas.email}</Typography>
          </Grid>
          <Grid item xs={12} display={'flex'} flexDirection={'row'}>
            <Box marginRight={1}>
              <PushPinOutlinedIcon />
            </Box>
            <Box>
              <Typography variant={'body2'} fontWeight={600}>
                Atendimento:
              </Typography>
              <Typography variant={'body2'}>
                {creas.abrangencia.map((a) => capitalize(a.bairro)).join(', ')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <DialogAlert
        title='Excluir'
        contentText={`Tem certeza que deseja excluir o CREAS?`}
        open={openExcluded}
        onSuccess={handleDeleteDialog}
        onClose={() => setOpenExcluded(false)}
      />
    </Box>
  )
}
